/**
 * publisher.ts — the v4-S9 usage-rollup PUBLISHER (opt-in, numbers only).
 *
 * Folds the local rollups.json sessions into per-model wire rows and POSTs
 * them to `POST /api/sync/usage` (bearer room token, same token the CLI uses
 * for /api/sync/command). The wire schema is explicit and CLOSED: token
 * counts, model names and health counters ONLY — the payload is built by
 * construction from an allowlist of keys, so no cwd, no repo, no branch, no
 * session ids and no transcript paths can ever ride along (the local
 * `usage --by-repo` breakdown never leaves the machine).
 *
 * Consent is double-gated:
 *   - locally: the project entry in ~/.aethereum/config.json must carry
 *     `usageRollups: true` (set by the `aethereum init` prompt). Anything else
 *     is a silent no-op — zero network calls.
 *   - server-side: the room's `share_usage_metrics` flag is enforced by the
 *     endpoint (403 when off) — the zero-outbound guarantee is server-checked,
 *     not client-trusted. On a 403 we disable nothing locally, but we record
 *     `usageLastAttemptAt` so the hook path does not hammer a closed door.
 *
 * Watermark design: `usageLastPublishedAt` lives on the SAME project entry as
 * the consent flag (per project == per room token, so two rooms never share a
 * watermark). A publish covers (lastPublishedAt, now]; sessions are bucketed
 * by their last observed activity, so periods never overlap or double-count.
 * The watermark only advances on a 200.
 *
 * Everything here is fail-soft and bounded (short AbortSignal.timeout,
 * mirroring the heartbeat in src/hive/auto.ts): a publish failure must NEVER
 * block or break a hook, an agent or a commit.
 */

import { homedir } from "node:os";
import {
  type RollupsFile,
  type SessionRollup,
  readRollups,
} from "./index-store.js";
import { readConfig, resolveProject, writeConfig } from "../hive/config-store.js";

// ── The wire schema (closed; mirrors apps/web/app/api/sync/usage/route.ts) ──

/** EXACTLY the keys POST /api/sync/usage accepts (.strict() on the server). */
export interface WireRollup {
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  correctionLoopCount: number;
  compactionCount: number;
  sessionCount: number;
  /** The latest `aethereum distill` scan's candidate count (v4-S11). Carried on
   *  the top row only, and only when the scan happened inside this publish
   *  period, so server-side sums stay honest (0 when never scanned). */
  distillCandidateCount: number;
  periodStart: string;
  periodEnd: string;
}

export interface RollupPayload {
  rollups: WireRollup[];
}

/** The server caps a batch at 20 rows; we keep the top rows by token volume. */
const MAX_WIRE_ROWS = 20;

/** Server rate limit is 12/hr/token; the auto (hook) path publishes at most
 *  once per this interval so routine stops never brush the limit. */
const AUTO_MIN_INTERVAL_MS = 10 * 60_000;

/** Mirror the heartbeat's fire-and-forget abort budget (src/hive/auto.ts). */
const PUBLISH_TIMEOUT_MS = 2_500;

const nonneg = (v: unknown): number =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.round(v) : 0;

/** A session's last observed activity — the timestamp it is bucketed by. */
function activityEnd(s: SessionRollup): number {
  return s.endedAt ?? s.startedAt ?? 0;
}

/**
 * Fold the local rollups into per-model wire rows for the period
 * (sincePeriod, now]. PURE: no filesystem, no network, no clock (when `now`
 * is passed). Returns null when there is nothing to publish.
 *
 * Privacy by construction: every row is built as an object literal of ONLY
 * the WireRollup keys — nothing from the session (cwd, repo, branch, title,
 * session id, transcript path) can leak into the payload.
 */
export function buildRollupPayload(
  rollups: RollupsFile,
  sincePeriod: number,
  now: number = Date.now(),
): RollupPayload | null {
  interface Bucket {
    input: number;
    output: number;
    cacheRead: number;
    cacheCreation: number;
    corrections: number;
    compactions: number;
    sessions: number;
  }
  const buckets = new Map<string, Bucket>();
  const bucket = (model: string): Bucket => {
    let b = buckets.get(model);
    if (!b) {
      b = { input: 0, output: 0, cacheRead: 0, cacheCreation: 0, corrections: 0, compactions: 0, sessions: 0 };
      buckets.set(model, b);
    }
    return b;
  };

  let earliest = Number.POSITIVE_INFINITY;
  for (const session of Object.values(rollups.sessions)) {
    const end = activityEnd(session);
    if (end <= sincePeriod || end > now) continue;

    // Token totals fold per model; session-level health counters attribute to
    // the session's dominant model (most tokens), or "unknown" when the
    // session carries counters but no priced model rows.
    let dominant: string | null = null;
    let dominantTokens = -1;
    for (const [model, t] of Object.entries(session.models ?? {})) {
      const input = nonneg(t.input);
      const output = nonneg(t.output);
      const cacheRead = nonneg(t.cacheRead);
      const cacheCreation = nonneg(t.cacheCreation);
      const total = input + output + cacheRead + cacheCreation;
      if (total === 0) continue;
      const b = bucket(model);
      b.input += input;
      b.output += output;
      b.cacheRead += cacheRead;
      b.cacheCreation += cacheCreation;
      if (total > dominantTokens) {
        dominantTokens = total;
        dominant = model;
      }
    }
    const corrections = nonneg(session.heuristics?.correctionLoopCount);
    const compactions = nonneg(session.heuristics?.compactionCount);
    if (dominant === null && corrections === 0 && compactions === 0) continue; // nothing to say
    const b = bucket(dominant ?? "unknown");
    b.corrections += corrections;
    b.compactions += compactions;
    b.sessions += 1;
    if (session.startedAt !== undefined && session.startedAt < earliest) earliest = session.startedAt;
    if (end < earliest) earliest = end;
  }

  if (buckets.size === 0) return null;

  // First publish (no watermark yet): the period opens at the earliest
  // included activity, not the epoch.
  const periodStart = new Date(
    sincePeriod > 0 ? sincePeriod : Number.isFinite(earliest) ? earliest : now,
  ).toISOString();
  const periodEnd = new Date(now).toISOString();

  // v4-S11: the latest distill scan's candidate count rides the TOP row only,
  // and only when the scan ran inside this publish window — the dashboard SUMS
  // distill_candidate_count across rows, so anything else would double-count.
  // Never scanned (no meta) → 0, honestly.
  const distill = rollups.meta?.distill;
  const distillCount =
    distill && distill.scannedAt > sincePeriod && distill.scannedAt <= now
      ? nonneg(distill.candidateCount)
      : 0;

  const rows: WireRollup[] = [...buckets.entries()]
    .sort(
      (a, b) =>
        b[1].input + b[1].output + b[1].cacheRead + b[1].cacheCreation -
        (a[1].input + a[1].output + a[1].cacheRead + a[1].cacheCreation),
    )
    .slice(0, MAX_WIRE_ROWS)
    // ONLY the allowed keys, by construction.
    .map(([model, b], i) => ({
      model,
      inputTokens: b.input,
      outputTokens: b.output,
      cacheReadTokens: b.cacheRead,
      cacheCreationTokens: b.cacheCreation,
      correctionLoopCount: b.corrections,
      compactionCount: b.compactions,
      sessionCount: b.sessions,
      distillCandidateCount: i === 0 ? distillCount : 0,
      periodStart,
      periodEnd,
    }));

  return { rollups: rows };
}

// ── publishUsage ─────────────────────────────────────────────────────────────

export type PublishStatus =
  /** 200 — rows accepted; the watermark advanced. */
  | "published"
  /** No local consent (`usageRollups !== true`) or no token: silent no-op, ZERO fetches. */
  | "declined"
  /** Consent on but nothing new since the watermark: no fetch. */
  | "nothing"
  /** Auto path within the min interval of the last attempt/publish: no fetch. */
  | "throttled"
  /** 403 — the room flag is off (or the token was revoked). Nothing changes locally. */
  | "flag-off"
  /** 429 — the endpoint rate limit; try again later. */
  | "rate-limited"
  /** Network error / timeout / unexpected status. */
  | "offline";

export interface PublishResult {
  status: PublishStatus;
  /** Rows accepted by the server (status "published"). */
  inserted?: number;
  /** Rows we sent (status "published"). */
  rows?: number;
}

export interface PublishUsageOptions {
  cwd: string;
  fetchImpl?: typeof fetch;
  /** Home dir override (tests). */
  home?: string;
  /** Clock override (tests). */
  now?: number;
  /** Manual `usage --publish`: bypass the auto-path throttle. */
  force?: boolean;
}

/** Patch the watermark fields on the project entry the consent lives on. */
function patchProjectEntry(
  home: string,
  projectPath: string,
  patch: { usageLastPublishedAt?: number; usageLastAttemptAt?: number },
): void {
  try {
    const cfg = readConfig(home);
    const entry = projectPath === "default" ? cfg.default : cfg.projects[projectPath];
    if (!entry) return;
    Object.assign(entry, patch);
    writeConfig(cfg, home);
  } catch {
    // fail-soft: a lost watermark write means at worst an early retry
  }
}

/**
 * Publish the usage rollups for the project at `cwd`. NEVER throws; a hook
 * caller races this against its remaining budget and ignores the result.
 * Unless local consent (`usageRollups: true`) AND a token exist, this returns
 * without touching the network at all — that is the privacy assertion.
 */
export async function publishUsage(opts: PublishUsageOptions): Promise<PublishResult> {
  try {
    const home = opts.home ?? homedir();
    const now = opts.now ?? Date.now();
    const fetchImpl = opts.fetchImpl ?? fetch;

    const project = resolveProject(opts.cwd, home);
    if (!project || project.usageRollups !== true || !project.token || !project.url) {
      return { status: "declined" };
    }

    const lastPublished = nonneg(project.usageLastPublishedAt);
    const lastAttempt = nonneg(project.usageLastAttemptAt);
    if (!opts.force && now - Math.max(lastPublished, lastAttempt) < AUTO_MIN_INTERVAL_MS) {
      return { status: "throttled" };
    }

    const payload = buildRollupPayload(readRollups(home), lastPublished, now);
    if (!payload) return { status: "nothing" };

    let res: Response;
    try {
      res = await fetchImpl(`${project.url.replace(/\/$/, "")}/api/sync/usage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${project.token}`,
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(PUBLISH_TIMEOUT_MS),
      });
    } catch {
      return { status: "offline" };
    }

    if (res.ok) {
      let inserted = payload.rollups.length;
      try {
        const body = (await res.json()) as { inserted?: number };
        if (typeof body.inserted === "number") inserted = body.inserted;
      } catch {
        // a 200 with an unreadable body still advanced the server; keep going
      }
      patchProjectEntry(home, project.projectPath, { usageLastPublishedAt: now });
      return { status: "published", inserted, rows: payload.rollups.length };
    }

    if (res.status === 403) {
      // Server-enforced opt-out (room flag off, or the token was revoked).
      // Disable NOTHING locally — the dashboard flag is the source of truth —
      // but remember the attempt so the auto path backs off.
      patchProjectEntry(home, project.projectPath, { usageLastAttemptAt: now });
      return { status: "flag-off" };
    }
    if (res.status === 429) {
      patchProjectEntry(home, project.projectPath, { usageLastAttemptAt: now });
      return { status: "rate-limited" };
    }
    return { status: "offline" };
  } catch {
    return { status: "offline" };
  }
}
